import type React from 'react';
import { ApplicationFormData } from '@/types/application';
import { SavedProfilePicker } from './SavedProfilePicker';

interface ProfileSelectionStepProps {
  formData: ApplicationFormData;
  errors?: any;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors?: (errors: any) => void;
  draftApplicationId?: number | null;
  applicationData?: any;
  onProfileSelected?: (profile: any) => void;
}

export const ProfileSelectionStep: React.FC<ProfileSelectionStepProps> = ({
  formData: _formData,
  updateFormData,
  onProfileSelected,
  errors: _errors,
  setErrors: _setErrors,
  draftApplicationId: _draftApplicationId,
  applicationData: _applicationData,
}) => {
  const handleProfileSelect = (profile: any) => {
    // Map profile data to form data
    const formatDateForInput = (date: string | Date | null | undefined): string => {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };

    const formatStartDate = (date: string | Date | null | undefined): string => {
      if (!date) return '';
      if (typeof date === 'string') {
        const trimmed = date.trim();
        if (!trimmed) return '';
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          const parsed = new Date(trimmed);
          return isNaN(parsed.getTime()) ? trimmed : parsed.toISOString().split('T')[0];
        }
        return trimmed;
      }
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
    };

    // Helper to safely get value (convert null/undefined to empty string for form fields)
    const getValue = (value: any, defaultValue: any = '') => {
      if (value === null || value === undefined) return defaultValue;
      return value;
    };

    const mappedData: Partial<ApplicationFormData> = {
      // A. QUALIFICATION
      programmeStrand: getValue(profile.programmeStrand),
      programmeStartDate: formatStartDate(profile.programmeStartDate),
      programmeEndDate: formatDateForInput(profile.programmeEndDate),
      studiedAtKalandraBefore: getValue(profile.studiedAtKalandraBefore, false),
      howDidYouFindOut: getValue(profile.howDidYouFindOut),
      howDidYouFindOutWhen: getValue(profile.howDidYouFindOutWhen),
      
      // B. PERSONAL DETAILS
      firstName: getValue(profile.firstName),
      lastName: getValue(profile.lastName),
      middleName: getValue(profile.middleName),
      preferredTitle: getValue(profile.preferredTitle),
      dateOfBirth: formatDateForInput(profile.dateOfBirth),
      gender: getValue(profile.gender),
      nsn: getValue(profile.nsn),
      countryOfCitizenship: getValue(profile.countryOfCitizenship),
      email: getValue(profile.email),
      ethnicity: getValue(profile.ethnicity),
      passportNumber: getValue(profile.passportNumber),
      passportExpiryDate: formatDateForInput(profile.passportExpiryDate),
      iwi: getValue(profile.iwi),
      priorActivity: getValue(profile.priorActivity),
      disability: getValue(profile.disability, false),
      disabilityDetails: getValue(profile.disabilityDetails),
      convictionChecks: getValue(profile.convictionChecks, false),
      convictionDetails: getValue(profile.convictionDetails),
      firstLanguage: getValue(profile.firstLanguage),
      maritalStatus: getValue(profile.maritalStatus),
      
      // C. CONTACT DETAILS - All fields explicitly set
      homeAddress: getValue(profile.homeAddress),
      homePhone: getValue(profile.homePhone),
      homeMobile: getValue(profile.homeMobile),
      homeFax: getValue(profile.homeFax),
      homeAccommodationType: getValue(profile.homeAccommodationType),
      studyAddress: getValue(profile.studyAddress),
      studyPhone: getValue(profile.studyPhone),
      studyMobile: getValue(profile.studyMobile),
      studyFax: getValue(profile.studyFax),
      studyAccommodationType: getValue(profile.studyAccommodationType),
      nextOfKinName: getValue(profile.nextOfKinName),
      nextOfKinPhone: getValue(profile.nextOfKinPhone),
      nextOfKinMobile: getValue(profile.nextOfKinMobile),
      nextOfKinFax: getValue(profile.nextOfKinFax),
      nextOfKinEmail: getValue(profile.nextOfKinEmail),
      nextOfKinAddress: getValue(profile.nextOfKinAddress),
      emergencyContactName: getValue(profile.emergencyContactName),
      emergencyContactPhone: getValue(profile.emergencyContactPhone),
      emergencyContactMobile: getValue(profile.emergencyContactMobile),
      emergencyContactEmail: getValue(profile.emergencyContactEmail),
      emergencyContactAddress: getValue(profile.emergencyContactAddress),
      
      // D. ACADEMIC INFORMATION
      secondarySchoolName: getValue(profile.secondarySchoolName),
      secondarySchoolLastYear: getValue(profile.secondarySchoolLastYear),
      secondarySchoolHighestLevel: getValue(profile.secondarySchoolHighestLevel),
      firstYearTertiaryEnrolment:
        profile.firstYearTertiaryEnrolment === true || profile.firstYearTertiaryEnrolment === false
          ? profile.firstYearTertiaryEnrolment
          : null,
      tertiaryInstitutionName: getValue(profile.tertiaryInstitutionName),
      tertiaryInstitutionYear: getValue(profile.tertiaryInstitutionYear),
      englishLevel: getValue(profile.englishLevel),
      englishExamType: getValue(profile.englishExamType),
      englishExamDate: formatDateForInput(profile.englishExamDate),
      englishOverallScore: getValue(profile.englishOverallScore),
      englishListeningScore: getValue(profile.englishListeningScore),
      englishReadingScore: getValue(profile.englishReadingScore),
      englishWritingScore: getValue(profile.englishWritingScore),
      englishSpeakingScore: getValue(profile.englishSpeakingScore),
      hasEnglishScores: getValue(profile.hasEnglishScores, false),
      careerIntentions: getValue(profile.careerIntentions),
      
      // D. WORK HISTORY
      workedAsHealthCareAssistant: getValue(profile.workedAsHealthCareAssistant, false),
      healthCareAssistantDuration: getValue(profile.healthCareAssistantDuration),
      otherHealthcareQualifications: getValue(profile.otherHealthcareQualifications, false),
      healthcareQualificationsDetails: getValue(profile.healthcareQualificationsDetails),
      continueStudiesOrWorkforce: getValue(profile.continueStudiesOrWorkforce),
      
      // E. ADDITIONAL INFORMATION
      whyDoYouWantToDoProgramme: getValue(profile.whyDoYouWantToDoProgramme),
      
      // Legacy fields - All explicitly set
      phoneNumber: getValue(profile.phoneNumber),
      address: getValue(profile.address),
      city: getValue(profile.city),
      province: getValue(profile.province),
      country: getValue(profile.country),
      postalCode: getValue(profile.postalCode),
      currentEducation: getValue(profile.currentEducation),
      levelOfEducation: getValue(profile.levelOfEducation),
      institutionName: getValue(profile.institutionName),
      institutionCountry: getValue(profile.institutionCountry),
      graduationDate: formatDateForInput(profile.graduationDate),
      degreeName: getValue(profile.degreeName),
      gpa: getValue(profile.gpa),
      gradingScheme: getValue(profile.gradingScheme),
      hasGREScores: getValue(profile.hasGREScores, false),
      greExamDate: formatDateForInput(profile.greExamDate),
      greOverallScore: getValue(profile.greOverallScore),
      greVerbalScore: getValue(profile.greVerbalScore),
      greQuantitativeScore: getValue(profile.greQuantitativeScore),
      greAnalyticalScore: getValue(profile.greAnalyticalScore),
      hasGMATScores: getValue(profile.hasGMATScores, false),
      gmatExamDate: formatDateForInput(profile.gmatExamDate),
      gmatTotalScore: getValue(profile.gmatTotalScore),
      hasSATScores: getValue(profile.hasSATScores, false),
      satExamDate: formatDateForInput(profile.satExamDate),
      satOverallScore: getValue(profile.satOverallScore),
      satMathScore: getValue(profile.satMathScore),
      satReadingScore: getValue(profile.satReadingScore),
      satWritingScore: getValue(profile.satWritingScore),
      // Preferences
      courseOfInterest: getValue(profile.courseOfInterest),
      countryOfChoice: getValue(profile.countryOfChoice),
      budget: getValue(profile.budget),
      preferredStartDate: formatDateForInput(profile.preferredStartDate),
      studyMode: getValue(profile.studyMode),
      // Additional Notes
      notes: getValue(profile.notes),
      // Documents
      documents: getValue(profile.documents),
    };

    updateFormData(mappedData);
    onProfileSelected?.(profile);
  };

  return <SavedProfilePicker onSelect={handleProfileSelect} />;
};
